import { Assert } from "./Assert";
import { EntityCommand, EntityCommandPropertyValues, EntityCommandTypeEnum, IEntityCommand } from "./EntityCommand";
import { EntityCommandInformation, IEntityCommandInformation } from "./EntityCommandInformation";
import { MethodEvaluator } from "./Evaluation/MethodEvaluator";
import { PartialNullable } from "./Extensions";
import { StringHelper } from "./Helpers/StringHelper";
import { NAMESPACE } from "./_Namespace";
import { Property } from "./RadObject";
import { WindowShim } from "./RadWindow";
import { PermissionEvaluator } from "./Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { WebApiClientService } from "./Server/WebApiClientService";
import { ServiceManager } from "./ServiceManager";
import { ButtonStyleEnum } from "./Ui/Controls/ButtonStyleEnum";
import { MessageDialog } from "./Ui/Controls/MessageDialog";
import { DialogResultEnum } from "./Ui/Dialogs/DialogResultEnum";

const
    radWindow = WindowShim();

(radWindow as any).EntityCommandDelete = {
    deleteVisibilityEvaluatorMethod: function (methodEvaluator: MethodEvaluator): boolean
    {
        const
            entityCommandInformation = methodEvaluator.state.get() as IEntityCommandInformation;

        if (!entityCommandInformation)
            return false;

        const
            caller = entityCommandInformation.caller.get();

        if (!caller)
            return false;

        const
            record = entityCommandInformation.record.get();

        if (!record)
            return false;

        const
            entityName = entityCommandInformation.entityName.get();

        if (entityName === "LookupItem")
        {
            if (record?.isLinkedToEnum)
                return false;
        }

        return record.deletedGuid ? false : true;
    },
};

export type EntityCommandDeletePropertyValues =
    EntityCommandPropertyValues
    & {
    };

export type EntityCommandDeleteProperties = {
    [name in keyof EntityCommandDeletePropertyValues]: Property<EntityCommandDeletePropertyValues[name]>
};

export interface IEntityCommandDelete extends IEntityCommand, EntityCommandDeleteProperties
{
}


export class EntityCommandDelete extends EntityCommand<EntityCommandDeletePropertyValues> implements IEntityCommandDelete
{
    public static override classNamespace = NAMESPACE + "EntityCommandDelete";
    public static override className = "EntityCommandDelete";

    // #region Static

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntityCommandDeletePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                description: "Delete",
                key: "DELETE",
                permission: PermissionEvaluator.permissionLevels.delete,
                type: EntityCommandTypeEnum.entity,
                visibleEvaluator: new MethodEvaluator({
                    methodPath: "EntityCommandDelete.deleteVisibilityEvaluatorMethod",
                }),
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected messageDialog?: MessageDialog;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntityCommandDeletePropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Overrides

    protected override _executeAsync(entityCommandInformation: EntityCommandInformation, value: any)
    {
        return (
            super._executeAsync(entityCommandInformation, value)
                .then(() =>
                {
                    if (this.messageDialog === undefined)
                        this.messageDialog = new MessageDialog();

                    const
                        entitySetName = entityCommandInformation.entitySetName.get(),
                        entityNameFriendly = entityCommandInformation.entityNameFriendly.get(),
                        record = entityCommandInformation.record.get();

                    Assert.isNotNullOrUndefined(entitySetName, "entitySetName");
                    Assert.isNotNullOrUndefined(entityNameFriendly, "entityNameFriendly");

                    if (!record || !record.id)
                        return;

                    this.messageDialog.buttonStyle.set(ButtonStyleEnum.yesNo);
                    this.messageDialog.title.set(StringHelper.format("Delete {0}?", entityNameFriendly));
                    this.messageDialog.text.set(StringHelper.format("Are you sure you want to delete this {0}?", entityNameFriendly.toLowerCase()));

                    return (
                        this.messageDialog
                            .openAsync()
                            .then(result =>
                            {
                                if (result.dialogResult === DialogResultEnum.yes)
                                {
                                    return (
                                        ServiceManager.getRequired(WebApiClientService)
                                            .createEntityDeleteRequest(
                                                entitySetName,
                                                record.id
                                            )
                                            .executeAsync()
                                    );
                                }

                                return Promise.reject("User cancelled");
                            })
                    );
                })
        );
    }

    // #endregion

    // #endregion
}

